import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowDownUp, Printer, Loader2, Lock } from "lucide-react";
import { ACTION_LABELS, changeText, stamp } from "@/lib/audit";

const esc = (t) => String(t ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function lines(e) {
  const out = e.changes?.length ? e.changes.map(changeText) : [];
  if (e.detail && !(e.action === "ticket_created" && out.length)) out.unshift(e.detail);
  return out;
}

function printAudit(ticket, entries, order) {
  const rows = entries.map((e) => `<tr><td style="white-space:nowrap">${esc(stamp(e.at))}</td><td>${esc(e.userName)}</td>
    <td><b>${esc(ACTION_LABELS[e.action] || e.action)}</b>${e.backfilled ? " <i>(rebuilt)</i>" : ""}<br/>${lines(e).map(esc).join("<br/>")}</td></tr>`).join("");
  const w = window.open("", "_blank");
  w.document.write(`<html><head><title>Audit ${esc(ticket)}</title></head><body style="font-family:Arial;padding:24px;font-size:12px">
    <h2>Ticket Audit Trail — ${esc(ticket)}</h2><p>${entries.length} entries · ${order === "desc" ? "newest" : "oldest"} first · printed ${esc(new Date().toLocaleString())}</p>
    <table border="1" cellpadding="6" style="border-collapse:collapse;width:100%"><tr><th>Timestamp</th><th>User</th><th>What changed</th></tr>${rows}</table></body></html>`);
  w.document.close();
  w.print();
}

export default function TicketAuditLog({ sessionId, refreshKey, order, setOrder }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/sessions/${sessionId}/audit?order=${order}`)
      .then(({ data: d }) => setData(d))
      .catch((e) => setData({ error: formatApiErrorDetail(e.response?.data?.detail) }));
  }, [sessionId, order, refreshKey]);

  if (!data) return <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-slate-500" />;
  if (data.error) return <p data-testid="audit-error" className="text-sm text-red-300">{data.error}</p>;

  return (
    <div data-testid="ticket-audit-log" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs text-slate-500"><Lock className="h-3.5 w-3.5" /> Read-only legal record · {data.entries.length} entries</p>
        <div className="flex gap-2">
          <Button data-testid="audit-order-toggle" variant="outline" onClick={() => setOrder(order === "desc" ? "asc" : "desc")}
            className="h-8 gap-1.5 border-slate-700 bg-slate-950 px-3 text-xs text-slate-200 hover:bg-slate-800">
            <ArrowDownUp className="h-3.5 w-3.5" /> {order === "desc" ? "Newest first" : "Oldest first"}
          </Button>
          <Button data-testid="audit-print" onClick={() => printAudit(data.ticketNumber, data.entries, order)}
            className="h-8 gap-1.5 bg-[#A0148C] px-3 text-xs font-bold hover:bg-[#80106F]">
            <Printer className="h-3.5 w-3.5" /> Print
          </Button>
        </div>
      </div>
      <ol className="space-y-2">
        {data.entries.map((e) => (
          <li key={e.id} data-testid={`audit-entry-${e.id}`} className="rounded-lg border border-slate-800 bg-slate-950 p-3">
            <p className="font-data text-[11px] text-slate-500">
              {stamp(e.at)} · <span className="text-slate-300">{e.userName}</span>
              {e.backfilled && <span className="ml-1 text-amber-400/80">(rebuilt)</span>}
            </p>
            <p className="text-sm font-semibold text-[#d946ef]">{ACTION_LABELS[e.action] || e.action}</p>
            {lines(e).map((l, i) => <p key={i} className="text-sm text-slate-300">{l}</p>)}
          </li>
        ))}
        {!data.entries.length && <p data-testid="audit-empty" className="py-6 text-center text-sm text-slate-500">No audit entries yet.</p>}
      </ol>
    </div>
  );
}
